/**
 * Every API call carries the selected avatar, so the server scopes the request
 * without the page ever handling a tenant id. The tenant comes from the session
 * cookie alone - a client-supplied one would be ignored.
 */
let currentAvatarId: string | null = null;

export const setAvatarId = (id: string | null) => {
  currentAvatarId = id;
};
export const getAvatarId = () => currentAvatarId;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly responseData?: unknown,
  ) {
    super(message);
  }
}

export async function api<T = any>(
  path: string,
  opts: {
    method?: string;
    body?: unknown;
    avatarId?: string | null;
    signal?: AbortSignal;
  } = {},
): Promise<T> {
  const avatarId =
    opts.avatarId === undefined ? currentAvatarId : opts.avatarId;
  const res = await fetch(path, {
    method: opts.method ?? (opts.body ? "POST" : "GET"),
    signal: opts.signal,
    headers: {
      "Content-Type": "application/json",
      ...(avatarId ? { "x-avatar-id": avatarId } : {}),
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok)
    throw new ApiError(
      res.status,
      data?.error ?? `request failed (${res.status})`,
      data,
    );
  return data as T;
}

export type Me = {
  workspaceCount?: number;
  communicationsEnabled?: boolean;
  notesDetailsEnabled?: boolean;
  user: {
    id: string;
    email: string;
    role: string;
    displayName: string;
    tenantId: string;
    passwordResetRequired: boolean;
  };
  tenant: {
    id: string;
    name: string;
    slug: string;
    openaiProjectId?: string;
    hasOwnKey: boolean;
    workspaceType?: "business" | "personal";
  };
  defaultAvatarId?: string;
  avatar: AvatarSummary | null;
  avatars: AvatarSummary[];
};

export type AvatarSummary = {
  id: string;
  slug: string;
  name: string;
  role: string;
  responsibility: string;
  voice: string;
  model: string;
  capabilities: string[];
  /** Part of every organisation's standing team; cannot be removed. */
  mandatory: boolean;
  avatarKind?: "representative" | "consultant" | "employee";
  managed?: boolean;
};
